import * as React from 'react';
import { useId, useRef } from 'react';
import {
  root as rootCls,
  tabStrip as tabStripCls,
  tab as tabCls,
  tabActive as tabActiveCls,
  tabIcon as tabIconCls,
  tabClose as tabCloseCls,
  metaStrip as metaStripCls,
  metaLeft as metaLeftCls,
  metaRight as metaRightCls,
  body as bodyCls,
} from './ArtifactWorkspace.css';

/**
 * Single open Deliverable in the tab strip.
 */
export interface ArtifactTab {
  /** Unique id; used as React key + close-callback arg. */
  id: string;
  /** Tab label — typically the Deliverable's name. */
  label: string;
  /**
   * Icon — typically a file-type glyph from the DS Icon registry rendered
   * via `<Icon as={FileText} />` (`primitives/Icon/icons`), never a bare
   * `lucide-react` import.
   */
  icon?: React.ReactNode;
}

export interface ArtifactWorkspaceProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  'onSelect'
> {
  /** Open tabs in left-to-right order. */
  tabs: ReadonlyArray<ArtifactTab>;
  /** Currently-active tab id. */
  activeId: string;
  /** Called when a tab is selected. */
  onSelect: (id: string) => void;
  /** Called when a tab's ✕ is clicked, or the tab is middle-clicked. */
  onClose?: (id: string) => void;
  /** Optional metadata-strip content (left side — Owner / Updated). */
  metaLeft?: React.ReactNode;
  /** Optional metadata-strip content (right side — status pill, primary action). */
  metaRight?: React.ReactNode;
  /** The active Deliverable's rendered body. */
  children: React.ReactNode;
  /** ref-as-prop. */
  ref?: React.Ref<HTMLElement>;
}

/**
 * ArtifactWorkspace — central column of the workspace shell. Hosts the
 * open-Deliverable tab strip + the active artifact's content.
 *
 * The DS owns the FRAME (tabs, metadata strip, status pill, optional
 * Send-to-Client action); the active deliverable's rendered body comes
 * through `children`. Each tab is a real `<div role="tab">` so the
 * close affordance below can be a true nested `<button>` without
 * violating WAI-ARIA's "no interactive descendants inside a button"
 * rule.
 *
 * Full WAI-ARIA `tablist` contract (mirrors the `Tabs` primitive):
 *   - each tab has a stable `id` + `aria-controls` → the panel;
 *   - the body panel has `role="tabpanel"`, a stable `id`, and
 *     `aria-labelledby` → the active tab;
 *   - roving `tabIndex` (only the active tab is in the tab order);
 *   - keyboard (handled on the tablist, automatic-activation pattern):
 *       · ← / ↑ — previous tab (wraps)
 *       · → / ↓ — next tab (wraps)
 *       · Home / End — first / last tab
 *       · Enter / Space — (re)activate the focused tab
 *     Arrow/Home/End move the active selection AND focus, so every tab
 *     (not just the active one) is reachable from the keyboard.
 *
 * Prototype: `http://localhost:8000/` workspace, central column.
 */
export function ArtifactWorkspace({
  tabs,
  activeId,
  onSelect,
  onClose,
  metaLeft,
  metaRight,
  children,
  ref,
  className,
  ...rest
}: ArtifactWorkspaceProps) {
  const cls = [rootCls, className].filter(Boolean).join(' ');
  const idPrefix = useId();
  const tabId = (id: string) => `${idPrefix}-tab-${id}`;
  const panelId = (id: string) => `${idPrefix}-panel-${id}`;
  const tabListRef = useRef<HTMLDivElement | null>(null);

  // Roving keyboard nav on the tablist (automatic-activation pattern,
  // mirroring the Tabs primitive). Arrow/Home/End move the active selection
  // AND focus, so every tab is reachable — not just the active one.
  const onTabListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (tabs.length === 0) return;
    const i = tabs.findIndex((t) => t.id === activeId);
    if (i === -1) return;
    let next = i;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
      next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    else return;
    e.preventDefault();
    // i !== -1 guarantees tabs.length > 0 and `next` is in range.
    const target = tabs[next]!;
    onSelect(target.id);
    tabListRef.current?.querySelector<HTMLDivElement>(`[data-tab-id="${target.id}"]`)?.focus();
  };

  return (
    <section ref={ref} className={cls} aria-label="Active deliverables" {...rest}>
      <div
        className={tabStripCls}
        role="tablist"
        aria-label="Open deliverables"
        ref={tabListRef}
        onKeyDown={onTabListKeyDown}
        // The tablist receives the roving keyboard events but never holds
        // focus itself — focus lives on the active tab. tabIndex=-1 keeps it
        // out of the tab order while satisfying the "interactive role must be
        // focusable" rule (mirrors the Tabs primitive).
        tabIndex={-1}
      >
        {tabs.map((t) => {
          const active = t.id === activeId;
          const tabClassName = [tabCls, active ? tabActiveCls : ''].filter(Boolean).join(' ');
          const handleSelect = () => onSelect(t.id);
          return (
            <div
              key={t.id}
              role="tab"
              id={tabId(t.id)}
              aria-controls={panelId(t.id)}
              data-tab-id={t.id}
              tabIndex={active ? 0 : -1}
              aria-selected={active}
              className={tabClassName}
              onClick={handleSelect}
              // Middle-click closes, like a browser tab. Suppress the
              // middle-button mousedown too, or the browser enters autoscroll.
              onMouseDown={onClose ? (e) => e.button === 1 && e.preventDefault() : undefined}
              onAuxClick={
                onClose
                  ? (e) => {
                      if (e.button !== 1) return;
                      e.preventDefault();
                      onClose(t.id);
                    }
                  : undefined
              }
              onKeyDown={(e) => {
                // Enter / Space (re)activate the focused tab. Arrow/Home/End
                // bubble up to the tablist handler for roving navigation.
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect();
                }
              }}
            >
              {t.icon ? <span className={tabIconCls}>{t.icon}</span> : null}
              <span>{t.label}</span>
              {onClose ? (
                <button
                  type="button"
                  aria-label={`Close ${t.label}`}
                  className={tabCloseCls}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose(t.id);
                  }}
                >
                  <span aria-hidden="true">✕</span>
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {(metaLeft || metaRight) && (
        <div className={metaStripCls}>
          <div className={metaLeftCls}>{metaLeft}</div>
          <div className={metaRightCls}>{metaRight}</div>
        </div>
      )}

      <div
        className={bodyCls}
        role="tabpanel"
        id={panelId(activeId)}
        aria-labelledby={tabId(activeId)}
        tabIndex={0}
      >
        {children}
      </div>
    </section>
  );
}
ArtifactWorkspace.displayName = 'ArtifactWorkspace';
